import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthState, ClientPrincipal } from '../types/auth';
import { Roles } from '../utils/roles';

interface AuthContextValue extends AuthState {
  hasRole: (role: string | string[]) => boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function fetchClientPrincipal(): Promise<ClientPrincipal | null> {
  try {
    const response = await fetch('/.auth/me', { credentials: 'include' });
    
    // Check if we're in local dev mode (localhost)
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (!response.ok) {
      // In local dev mode without SWA CLI, this endpoint doesn't exist
      if (response.status === 404 && isLocalDev) {
        console.warn('Running in local dev mode without authentication');
        return createMockPrincipal();
      }
      // A deployed 404 means Static Web Apps has no auth handler. That happens
      // when the config registers Entra but AZURE_CLIENT_ID / AAD_CLIENT_SECRET
      // were never set. Treat it as signed out so the shell can offer Sign in.
      if (response.status === 404) {
        console.warn('Static Web App auth is not configured; continuing signed out');
        return null;
      }
      throw new Error(`Failed to load auth context (${response.status})`);
    }

    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      // Only use mock in local dev, not in deployed environments
      if (isLocalDev) {
        console.warn('Running in local dev mode without authentication');
        return createMockPrincipal();
      }
      throw new Error('Authentication endpoint returned invalid content type');
    }

    const payload = (await response.json()) as { clientPrincipal?: ClientPrincipal };
    return payload.clientPrincipal ?? null;
  } catch (error) {
    // Network error or parsing error in local dev only
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if ((error instanceof TypeError || error instanceof SyntaxError) && isLocalDev) {
      console.warn('Running in local dev mode without authentication');
      return createMockPrincipal();
    }
    throw error;
  }
}

function createMockPrincipal(): ClientPrincipal {
  // Mock user for local development
  return {
    userId: 'local-dev-user',
    userDetails: 'developer@local.dev',
    identityProvider: 'aad',
    userRoles: ['authenticated', 'group_admin', 'group_reviewer', 'group_reader']
  };
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }: AuthProviderProps) => {
  const [state, setState] = useState<AuthState>({ principal: null, isLoading: true });

  const loadPrincipal = useCallback(async () => {
    setState((prev: AuthState) => ({ ...prev, isLoading: true, error: undefined }));
    try {
      const principal = await fetchClientPrincipal();
      setState({ principal, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown authentication error';
      setState({ principal: null, isLoading: false, error: message });
    }
  }, []);

  useEffect(() => {
    void loadPrincipal();
  }, [loadPrincipal]);

  const hasRole = useMemo(
    () =>
      (role: string | string[]) => {
        const roles = Array.isArray(role) ? role : [role];
        const principalRoles = state.principal?.userRoles ?? [];
        if (principalRoles.includes(Roles.Anonymous)) {
          return roles.some((candidate) => candidate === Roles.Anonymous);
        }
        return roles.some((candidate) => principalRoles.includes(candidate));
      },
    [state.principal]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      hasRole,
      refresh: loadPrincipal
    }),
    [state, hasRole, loadPrincipal]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
